const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: 3D Coordinate System & Partial Derivatives ──────────
function DiagramPartialDerivatives() {
  const W = 900, H = 380;

  // Isometric 3D axes (left panel)
  const ox = 160, oy = 260;
  const isoX = (x, y, z) => ox + x * 44 - y * 28;
  const isoY = (x, y, z) => oy - z * 44 + y * 18;

  // Surface grid z = 2 - 0.3x^2 - 0.2y^2 (bowl shape)
  const surf = (x, y) => 2.8 - 0.28 * x * x - 0.22 * y * y;

  const gridLines = [];
  for (let xi = -2; xi <= 2; xi++) {
    const pts = [];
    for (let yi = -2; yi <= 2; yi += 0.5) {
      pts.push([isoX(xi, yi, surf(xi, yi)), isoY(xi, yi, surf(xi, yi))]);
    }
    gridLines.push(pts);
  }
  const gridLinesY = [];
  for (let yi = -2; yi <= 2; yi++) {
    const pts = [];
    for (let xi = -2; xi <= 2; xi += 0.5) {
      pts.push([isoX(xi, yi, surf(xi, yi)), isoY(xi, yi, surf(xi, yi))]);
    }
    gridLinesY.push(pts);
  }

  const toPath = pts => pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');

  // Point P at (1, 1, surf(1,1))
  const px = 1, py = 1, pz = surf(1, 1);
  const Px = isoX(px, py, pz), Py = isoY(px, py, pz);

  // Partial wrt x tangent line (y fixed = 1, x from 0 to 2)
  const dfdx = -0.56 * px;
  const tx0 = isoX(0, py, surf(0, py) + dfdx * (0 - px));
  const ty0 = isoY(0, py, surf(0, py) + dfdx * (0 - px));
  const tx1 = isoX(2, py, surf(0, py) + dfdx * (2 - px));
  const ty1 = isoY(2, py, surf(0, py) + dfdx * (2 - px));

  // Partial wrt y tangent line (x fixed = 1, y from 0 to 2)
  const dfdy = -0.44 * py;
  const ty0b = isoY(px, 0, surf(px, 0) + dfdy * (0 - py));
  const tx0b = isoX(px, 0, surf(px, 0) + dfdy * (0 - py));
  const ty1b = isoY(px, 2, surf(px, 0) + dfdy * (2 - py));
  const tx1b = isoX(px, 2, surf(px, 0) + dfdy * (2 - py));

  // Right panel: formula cards
  const cards = [
    { y: 20, bg: '#EBF3FB', bc: '#2E75B6', title: 'Partial ∂f/∂x', lines: ['Differentiate w.r.t. x,', 'treat y as a constant'] },
    { y: 100, bg: '#f0fff4', bc: '#27ae60', title: 'Partial ∂f/∂y', lines: ['Differentiate w.r.t. y,', 'treat x as a constant'] },
    { y: 180, bg: '#fff7ee', bc: '#e07b00', title: 'Gradient ∇f', lines: ['∇f = (∂f/∂x, ∂f/∂y, ∂f/∂z)', 'Points in steepest ascent'] },
    { y: 260, bg: '#f5d0ff', bc: '#9b59b6', title: 'Critical Point', lines: ['∂f/∂x = 0 AND ∂f/∂y = 0', 'Use 2nd deriv test to classify'] },
  ];

  const px0 = 490, panW = W - px0 - 14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>3D Surfaces, Partial Derivatives & the Gradient</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Surface grid Y-lines */}
        {gridLinesY.map((pts, i) => (
          <path key={'gy' + i} d={toPath(pts)} fill="none" stroke="#b8d4f0" strokeWidth="1" />
        ))}
        {/* Surface grid X-lines */}
        {gridLines.map((pts, i) => (
          <path key={'gx' + i} d={toPath(pts)} fill="none" stroke="#cce0f5" strokeWidth="1.2" />
        ))}

        {/* Axes */}
        <line x1={ox} y1={oy} x2={isoX(3.2, 0, 0)} y2={isoY(3.2, 0, 0)} stroke="#1F3864" strokeWidth="1.8" />
        <line x1={ox} y1={oy} x2={isoX(0, 3.2, 0)} y2={isoY(0, 3.2, 0)} stroke="#1F3864" strokeWidth="1.8" />
        <line x1={ox} y1={oy} x2={isoX(0, 0, 3.8)} y2={isoY(0, 0, 3.8)} stroke="#1F3864" strokeWidth="1.8" />
        <text x={isoX(3.4, 0, 0)} y={isoY(3.4, 0, 0) + 4} fontSize="15" fill="#1F3864" fontWeight="700">x</text>
        <text x={isoX(0, 3.4, 0) - 14} y={isoY(0, 3.4, 0) + 4} fontSize="15" fill="#1F3864" fontWeight="700">y</text>
        <text x={isoX(0, 0, 3.9) - 8} y={isoY(0, 0, 3.9) - 4} fontSize="15" fill="#1F3864" fontWeight="700">z</text>

        {/* Tangent line ∂f/∂x (blue) */}
        <line x1={tx0} y1={ty0} x2={tx1} y2={ty1} stroke="#2E75B6" strokeWidth="2.5" strokeDasharray="7,3" />
        <text x={tx1 + 6} y={ty1 - 4} fontSize="12" fill="#2E75B6" fontWeight="700">∂f/∂x</text>

        {/* Tangent line ∂f/∂y (green) */}
        <line x1={tx0b} y1={ty0b} x2={tx1b} y2={ty1b} stroke="#27ae60" strokeWidth="2.5" strokeDasharray="7,3" />
        <text x={tx1b - 36} y={ty1b - 8} fontSize="12" fill="#27ae60" fontWeight="700">∂f/∂y</text>

        {/* Gradient arrow (orange) */}
        {(() => {
          const gx = -dfdx, gy = -dfdy;
          const scale = 28;
          const ex = Px + (isoX(px + gx * 0.6, py, pz) - isoX(px, py, pz));
          const ey = Py + (isoY(px + gx * 0.6, py, pz) - isoY(px, py, pz));
          return (
            <>
              <line x1={Px} y1={Py} x2={ex} y2={ey} stroke="#e07b00" strokeWidth="2.5" />
              <polygon points={`${ex},${ey} ${ex - 8},${ey - 4} ${ex - 6},${ey + 5}`} fill="#e07b00" />
              <text x={ex + 4} y={ey} fontSize="12" fill="#e07b00" fontWeight="700">∇f</text>
            </>
          );
        })()}

        {/* Point P */}
        <circle cx={Px} cy={Py} r={6} fill="#c0392b" stroke="#fff" strokeWidth="2" />
        <text x={Px + 8} y={Py - 8} fontSize="13" fill="#c0392b" fontWeight="700">P(1,1)</text>

        {/* Vertical drop line */}
        <line x1={Px} y1={Py} x2={isoX(px, py, 0)} y2={isoY(px, py, 0)} stroke="#aaa" strokeWidth="1" strokeDasharray="3,3" />

        {/* Right panel */}
        <rect x={px0} y={8} width={panW} height={H - 16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={px0 + panW / 2} y={30} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Key Concepts</text>
        {cards.map(({ y, bg, bc, title, lines }) => (
          <g key={title}>
            <rect x={px0 + 8} y={y + 36} width={panW - 16} height={70} rx={3} fill={bg} stroke={bc} strokeWidth="1" />
            <text x={px0 + 16} y={y + 52} fontSize="13" fill="#1F3864" fontWeight="700">{title}</text>
            {lines.map((ln, i) => (
              <text key={i} x={px0 + 16} y={y + 68 + i * 17} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        A function of two variables z = f(x,y) defines a surface in 3D space. Partial derivatives measure the slope in one direction while holding the other variable fixed — each is a tangent line on the surface. The gradient ∇f combines both partials into a vector pointing in the direction of steepest ascent.
      </div>
    </div>
  );
}

// ── Diagram 2: Level Curves & Contour Map ──────────────────────────
function DiagramLevelCurves() {
  const W = 900, H = 360;

  // Left: 3D bowl surface (isometric)
  const ox = 150, oy = 260;
  const isoX = (x, y, z) => ox + x * 42 - y * 26;
  const isoY = (x, y, z) => oy - z * 38 + y * 17;
  const surf = (x, y) => 3.0 - 0.35 * x * x - 0.35 * y * y;

  const levels = [0.5, 1.2, 1.9, 2.5];
  const levelColors = ['#c0392b', '#e07b00', '#27ae60', '#2E75B6'];

  // Right: 2D contour map
  const cx2 = 580, cy2 = 180, sc2 = 55;
  const tx2 = x => cx2 + x * sc2;
  const ty2 = y => cy2 - y * sc2;

  // For each level z = c, the circle is x^2+y^2 = (3-c)/0.35
  const contourRadii = levels.map(c => Math.sqrt((3.0 - c) / 0.35));

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Level Curves & Contour Maps — Reading the Topology of a Surface</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* ── Left: 3D surface with level planes ── */}
        <rect x={8} y={8} width={440} height={H - 16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={228} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">3D Surface with Horizontal Slices</text>

        {/* Surface grid */}
        {[-2, -1, 0, 1, 2].map(xi => {
          const pts = [];
          for (let yi = -2; yi <= 2; yi += 0.4) {
            const z = surf(xi, yi);
            if (z > 0) pts.push([isoX(xi, yi, z), isoY(xi, yi, z)]);
          }
          return <path key={'sx' + xi} d={pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')} fill="none" stroke="#b8d4f0" strokeWidth="1" />;
        })}
        {[-2, -1, 0, 1, 2].map(yi => {
          const pts = [];
          for (let xi = -2; xi <= 2; xi += 0.4) {
            const z = surf(xi, yi);
            if (z > 0) pts.push([isoX(xi, yi, z), isoY(xi, yi, z)]);
          }
          return <path key={'sy' + yi} d={pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')} fill="none" stroke="#cce0f5" strokeWidth="1.2" />;
        })}

        {/* Level curves on surface */}
        {levels.map((c, li) => {
          const r = contourRadii[li];
          if (r > 2.2) return null;
          const pts = [];
          for (let a = 0; a <= Math.PI; a += 0.15) {
            const xi = r * Math.cos(a), yi = r * Math.sin(a);
            pts.push([isoX(xi, yi, c), isoY(xi, yi, c)]);
          }
          return <path key={'lc' + li} d={pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')} fill="none" stroke={levelColors[li]} strokeWidth="2.5" />;
        })}

        {/* Axes */}
        <line x1={ox} y1={oy} x2={isoX(3, 0, 0)} y2={isoY(3, 0, 0)} stroke="#1F3864" strokeWidth="1.5" />
        <line x1={ox} y1={oy} x2={isoX(0, 3, 0)} y2={isoY(0, 3, 0)} stroke="#1F3864" strokeWidth="1.5" />
        <line x1={ox} y1={oy} x2={isoX(0, 0, 3.5)} y2={isoY(0, 0, 3.5)} stroke="#1F3864" strokeWidth="1.5" />
        <text x={isoX(3.2, 0, 0)} y={isoY(3.2, 0, 0) + 4} fontSize="14" fill="#1F3864" fontWeight="700">x</text>
        <text x={isoX(0, 3.2, 0) - 14} y={isoY(0, 3.2, 0) + 4} fontSize="14" fill="#1F3864" fontWeight="700">y</text>
        <text x={isoX(0, 0, 3.6) - 8} y={isoY(0, 0, 3.6) - 4} fontSize="14" fill="#1F3864" fontWeight="700">z</text>

        {/* Legend */}
        {levels.map((c, li) => (
          <g key={'leg' + li}>
            <line x1={20} y1={H - 90 + li * 20} x2={44} y2={H - 90 + li * 20} stroke={levelColors[li]} strokeWidth="2.5" />
            <text x={50} y={H - 85 + li * 20} fontSize="11" fill={levelColors[li]} fontWeight="700">z = {c}</text>
          </g>
        ))}

        {/* ── Right: 2D Contour Map ── */}
        <rect x={456} y={8} width={436} height={H - 16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={674} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">2D Contour Map (Top-Down View)</text>

        {/* Grid */}
        {[-2, -1, 0, 1, 2].map(v => (
          <g key={'g' + v}>
            <line x1={tx2(v)} y1={ty2(-2.5)} x2={tx2(v)} y2={ty2(2.5)} stroke="#eee" strokeWidth="1" />
            <line x1={tx2(-2.5)} y1={ty2(v)} x2={tx2(2.5)} y2={ty2(v)} stroke="#eee" strokeWidth="1" />
            <text x={tx2(v)} y={ty2(-2.5) + 14} textAnchor="middle" fontSize="10" fill="#bbb">{v}</text>
            <text x={tx2(-2.5) - 6} y={ty2(v) + 4} textAnchor="end" fontSize="10" fill="#bbb">{v}</text>
          </g>
        ))}

        {/* Axes */}
        <line x1={tx2(-2.5)} y1={cy2} x2={tx2(2.5)} y2={cy2} stroke="#1F3864" strokeWidth="1.5" />
        <line x1={cx2} y1={ty2(-2.5)} x2={cx2} y2={ty2(2.5)} stroke="#1F3864" strokeWidth="1.5" />
        <text x={tx2(2.5) + 6} y={cy2 + 4} fontSize="13" fill="#1F3864" fontWeight="700">x</text>
        <text x={cx2 + 5} y={ty2(2.5) - 4} fontSize="13" fill="#1F3864" fontWeight="700">y</text>

        {/* Contour circles */}
        {contourRadii.map((r, li) => {
          if (r > 2.6) return null;
          return (
            <g key={'cr' + li}>
              <circle cx={cx2} cy={cy2} r={r * sc2} fill="none" stroke={levelColors[li]} strokeWidth="2" />
              <text x={cx2 + r * sc2 + 4} y={cy2 - 4} fontSize="11" fill={levelColors[li]} fontWeight="700">z={levels[li]}</text>
            </g>
          );
        })}

        {/* Gradient arrow pointing outward (steepest descent toward centre) */}
        <line x1={cx2 + 30} y1={cy2 - 30} x2={cx2 + 6} y2={cy2 - 6} stroke="#e07b00" strokeWidth="2.5" />
        <polygon points={`${cx2 + 6},${cy2 - 6} ${cx2 + 16},${cy2 - 4} ${cx2 + 8},${cy2 - 16}`} fill="#e07b00" />
        <text x={cx2 + 34} y={cy2 - 34} fontSize="12" fill="#e07b00" fontWeight="700">−∇f</text>
        <text x={cx2 + 34} y={cy2 - 20} fontSize="10" fill="#e07b00">(steepest descent)</text>

        {/* Centre point (max) */}
        <circle cx={cx2} cy={cy2} r={5} fill="#9b59b6" stroke="#fff" strokeWidth="1.5" />
        <text x={cx2 + 8} y={cy2 - 8} fontSize="12" fill="#9b59b6" fontWeight="700">max</text>

        <text x={674} y={H - 22} textAnchor="middle" fontSize="11" fill="#555" fontStyle="italic">Tightly spaced contours = steep surface; widely spaced = gentle slope</text>
      </svg>
      <div style={S.dCaption}>
        A level curve is the set of all (x,y) where f(x,y) = c for some constant c. Stacking many level curves at different heights produces a contour map — the standard tool in engineering, topography, and FEA for visualising 3D surfaces in 2D. The gradient is always perpendicular to the level curves.
      </div>
    </div>
  );
}

// ── Diagram 3: Double Integral as Volume ──────────────────────────
function DiagramDoubleIntegral() {
  const W = 900, H = 370;

  const ox = 160, oy = 280;
  const isoX = (x, y, z) => ox + x * 46 - y * 30;
  const isoY = (x, y, z) => oy - z * 40 + y * 19;
  const surf = (x, y) => 1.2 + 0.4 * Math.sin(x * 0.9) * Math.cos(y * 0.9) + 0.3 * x + 0.15 * y;

  // Draw filled columns (Riemann sum visualization)
  const cols = [];
  for (let xi = 0; xi <= 1.5; xi += 0.5) {
    for (let yi = 0; yi <= 1.5; yi += 0.5) {
      const z = surf(xi + 0.25, yi + 0.25);
      cols.push({ xi, yi, z });
    }
  }

  const PW2 = 390;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Double Integral as Volume Under a Surface ∬f(x,y) dA</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* ── Left: 3D Riemann sum columns ── */}
        <rect x={8} y={8} width={440} height={H - 16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={228} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Volume Under Surface (Riemann Sum Approximation)</text>

        {/* Riemann columns */}
        {cols.map(({ xi, yi, z }, i) => {
          const x0 = xi, x1 = xi + 0.5, y0 = yi, y1 = yi + 0.5;
          // Four corners of the base, four of the top
          const bfl = [isoX(x0, y0, 0), isoY(x0, y0, 0)];
          const bfr = [isoX(x1, y0, 0), isoY(x1, y0, 0)];
          const bbl = [isoX(x0, y1, 0), isoY(x0, y1, 0)];
          const bbr = [isoX(x1, y1, 0), isoY(x1, y1, 0)];
          const tfl = [isoX(x0, y0, z), isoY(x0, y0, z)];
          const tfr = [isoX(x1, y0, z), isoY(x1, y0, z)];
          const tbl = [isoX(x0, y1, z), isoY(x0, y1, z)];
          const tbr = [isoX(x1, y1, z), isoY(x1, y1, z)];
          const alpha = 0.55 + i * 0.04;
          const blue = `rgba(46,117,182,${Math.min(alpha, 0.85)})`;
          const lightblue = `rgba(176,210,240,${Math.min(alpha, 0.7)})`;
          const faceTop = `${tfl[0]},${tfl[1]} ${tfr[0]},${tfr[1]} ${tbr[0]},${tbr[1]} ${tbl[0]},${tbl[1]}`;
          const faceRight = `${tfr[0]},${tfr[1]} ${tbr[0]},${tbr[1]} ${bbr[0]},${bbr[1]} ${bfr[0]},${bfr[1]}`;
          const faceFront = `${tfl[0]},${tfl[1]} ${tfr[0]},${tfr[1]} ${bfr[0]},${bfr[1]} ${bfl[0]},${bfl[1]}`;
          return (
            <g key={i}>
              <polygon points={faceFront} fill={lightblue} stroke="#2E75B6" strokeWidth="0.8" />
              <polygon points={faceRight} fill={blue} stroke="#2E75B6" strokeWidth="0.8" />
              <polygon points={faceTop} fill="#d0e8ff" stroke="#2E75B6" strokeWidth="0.8" />
            </g>
          );
        })}

        {/* Actual surface overlay */}
        {[0, 0.3, 0.6, 0.9, 1.2, 1.5, 1.8, 2.0].map(xi => {
          const pts = [];
          for (let yi = 0; yi <= 2.0; yi += 0.2) {
            pts.push([isoX(xi, yi, surf(xi, yi)), isoY(xi, yi, surf(xi, yi))]);
          }
          return <path key={'ss' + xi} d={pts.map((p, k) => `${k === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')} fill="none" stroke="#c0392b" strokeWidth="1.2" opacity="0.7" />;
        })}

        {/* Axes */}
        <line x1={ox} y1={oy} x2={isoX(3, 0, 0)} y2={isoY(3, 0, 0)} stroke="#1F3864" strokeWidth="1.6" />
        <line x1={ox} y1={oy} x2={isoX(0, 2.8, 0)} y2={isoY(0, 2.8, 0)} stroke="#1F3864" strokeWidth="1.6" />
        <line x1={ox} y1={oy} x2={isoX(0, 0, 3.2)} y2={isoY(0, 0, 3.2)} stroke="#1F3864" strokeWidth="1.6" />
        <text x={isoX(3.2, 0, 0)} y={isoY(3.2, 0, 0) + 5} fontSize="15" fill="#1F3864" fontWeight="700">x</text>
        <text x={isoX(0, 2.9, 0) - 14} y={isoY(0, 2.9, 0) + 5} fontSize="15" fill="#1F3864" fontWeight="700">y</text>
        <text x={isoX(0, 0, 3.3) - 8} y={isoY(0, 0, 3.3) - 4} fontSize="15" fill="#1F3864" fontWeight="700">z</text>

        {/* Volume label */}
        <text x={isoX(0.8, 0.8, 2.4)} y={isoY(0.8, 0.8, 2.4) - 12} fontSize="13" fill="#c0392b" fontWeight="700" fontFamily="'Courier New',monospace">f(x,y)</text>

        {/* ── Right: explanation panels ── */}
        <rect x={456} y={8} width={436} height={H - 16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={674} y={30} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Setup &amp; Interpretation</text>

        {[
          { y: 44, bg: '#EBF3FB', bc: '#2E75B6', title: 'Double Integral Definition', lines: ['∬_R f(x,y) dA', '= ∫∫ f(x,y) dx dy', 'Volume between surface and xy-plane'] },
          { y: 148, bg: '#f0fff4', bc: '#27ae60', title: 'Iterated Integral (rect. region)', lines: ['∫_a^b [ ∫_c^d f(x,y) dy ] dx', 'Inner integral: hold x constant', 'Outer integral: integrate result over x'] },
          { y: 248, bg: '#fff7ee', bc: '#e07b00', title: 'Order of Integration', lines: ['dA = dx dy  OR  dy dx', 'Fubini\'s Theorem: order can swap', 'when f is continuous on region R'] },
        ].map(({ y, bg, bc, title, lines }) => (
          <g key={title}>
            <rect x={464} y={y} width={420} height={92} rx={3} fill={bg} stroke={bc} strokeWidth="1" />
            <text x={472} y={y + 16} fontSize="13" fill="#1F3864" fontWeight="700">{title}</text>
            {lines.map((ln, i) => (
              <text key={i} x={472} y={y + 34 + i * 18} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The double integral ∬f(x,y) dA gives the signed volume between the surface z = f(x,y) and the xy-plane over a region R. It is evaluated as an iterated integral — integrate with respect to one variable first (treating the other as constant), then integrate that result. Fubini's Theorem guarantees the order can be swapped for continuous functions.
      </div>
    </div>
  );
}

// ── Diagram 4: Vector Fields & Line Integrals ──────────────────────
function DiagramVectorField() {
  const W = 900, H = 360;
  const cx = 220, cy = 180, sc = 52;
  const tx = x => cx + x * sc;
  const ty = y => cy - y * sc;

  // Vector field F = (-y, x) — rotational field
  const arrows = [];
  for (let xi = -2.5; xi <= 2.5; xi += 1) {
    for (let yi = -2.5; yi <= 2.5; yi += 1) {
      const fx = -yi * 0.35, fy = xi * 0.35;
      const len = Math.sqrt(fx * fx + fy * fy);
      if (len < 0.01) continue;
      const scale = 0.55 / (len + 0.3);
      arrows.push({ xi, yi, fx: fx * scale, fy: fy * scale });
    }
  }

  // Circular path
  const pathR = 1.5;
  const pathPts = [];
  for (let a = 0; a <= 2 * Math.PI + 0.1; a += 0.15) {
    pathPts.push([tx(pathR * Math.cos(a)), ty(pathR * Math.sin(a))]);
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Vector Fields & Line Integrals — Work Done Along a Path</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Left panel */}
        <rect x={8} y={8} width={440} height={H - 16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={228} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Vector Field F = (−y, x) — Rotational</text>

        {/* Grid */}
        {[-2, -1, 0, 1, 2].map(v => (
          <g key={v}>
            <line x1={tx(v)} y1={ty(-3)} x2={tx(v)} y2={ty(3)} stroke="#f0f0f0" strokeWidth="1" />
            <line x1={tx(-3)} y1={ty(v)} x2={tx(3)} y2={ty(v)} stroke="#f0f0f0" strokeWidth="1" />
            <text x={tx(v)} y={cy + 16} textAnchor="middle" fontSize="10" fill="#ccc">{v}</text>
            {v !== 0 && <text x={cx - 10} y={ty(v) + 4} textAnchor="end" fontSize="10" fill="#ccc">{v}</text>}
          </g>
        ))}

        {/* Axes */}
        <line x1={tx(-3)} y1={cy} x2={tx(3)} y2={cy} stroke="#1F3864" strokeWidth="1.5" />
        <line x1={cx} y1={ty(-3)} x2={cx} y2={ty(3)} stroke="#1F3864" strokeWidth="1.5" />
        <text x={tx(3) + 5} y={cy + 4} fontSize="13" fill="#1F3864" fontWeight="700">x</text>
        <text x={cx + 5} y={ty(3) - 4} fontSize="13" fill="#1F3864" fontWeight="700">y</text>

        {/* Vector arrows */}
        {arrows.map(({ xi, yi, fx, fy }, i) => {
          const x0 = tx(xi), y0 = ty(yi);
          const x1 = tx(xi + fx), y1 = ty(yi + fy);
          const dx = x1 - x0, dy = y1 - y0;
          const len = Math.sqrt(dx * dx + dy * dy);
          const ang = Math.atan2(dy, dx);
          const hx = x1 - 7 * Math.cos(ang), hy = y1 - 7 * Math.sin(ang);
          return (
            <g key={i}>
              <line x1={x0} y1={y0} x2={hx} y2={hy} stroke="#2E75B6" strokeWidth="1.5" />
              <polygon points={`${x1},${y1} ${x1 - 8 * Math.cos(ang - 0.4)},${y1 - 8 * Math.sin(ang - 0.4)} ${x1 - 8 * Math.cos(ang + 0.4)},${y1 - 8 * Math.sin(ang + 0.4)}`} fill="#2E75B6" />
            </g>
          );
        })}

        {/* Circular path */}
        <path d={pathPts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')} fill="none" stroke="#c0392b" strokeWidth="2.5" strokeDasharray="8,3" />

        {/* Direction arrow on path */}
        {(() => {
          const a = Math.PI / 4;
          const ax = tx(pathR * Math.cos(a)), ay = ty(pathR * Math.sin(a));
          const tang = Math.atan2(-Math.sin(a), Math.cos(a));
          return <polygon points={`${ax},${ay} ${ax - 11 * Math.cos(tang - 0.4)},${ay - 11 * Math.sin(tang - 0.4)} ${ax - 11 * Math.cos(tang + 0.4)},${ay - 11 * Math.sin(tang + 0.4)}`} fill="#c0392b" />;
        })()}
        <text x={tx(pathR + 0.15)} y={ty(0) - 8} fontSize="13" fill="#c0392b" fontWeight="700">C</text>

        {/* ── Right panel: formula cards ── */}
        <rect x={456} y={8} width={436} height={H - 16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={674} y={30} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Line Integral Concepts</text>

        {[
          { y: 44, bg: '#EBF3FB', bc: '#2E75B6', title: 'Line Integral of a Scalar', lines: ['∫_C f(x,y) ds', 'ds = arc length element', 'Gives "area of curtain" under f along C'] },
          { y: 148, bg: '#f0fff4', bc: '#27ae60', title: 'Line Integral of a Vector Field', lines: ['∫_C F · dr  =  ∫_C (P dx + Q dy)', 'F = (P, Q),  dr = (dx, dy)', 'Gives work done by F along path C'] },
          { y: 248, bg: '#fff7ee', bc: '#e07b00', title: "Green's Theorem (2D)", lines: ['∮_C (P dx + Q dy) = ∬_R (∂Q/∂x − ∂P/∂y) dA', 'Converts closed line integral', 'to a double integral over region R'] },
        ].map(({ y, bg, bc, title, lines }) => (
          <g key={title}>
            <rect x={464} y={y} width={420} height={92} rx={3} fill={bg} stroke={bc} strokeWidth="1" />
            <text x={472} y={y + 16} fontSize="13" fill="#1F3864" fontWeight="700">{title}</text>
            {lines.map((ln, i) => (
              <text key={i} x={472} y={y + 34 + i * 18} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        A vector field assigns a vector to every point in space — think of it as a map of forces, velocities, or field strengths. The line integral of a vector field along a path C gives the total work done by the field on an object travelling along C. Green's Theorem connects this to a double integral over the enclosed region.
      </div>
    </div>
  );
}

// ── Diagram 5: Optimization — Saddle Points & Constrained ─────────
function DiagramOptimization() {
  const W = 900, H = 360;
  const ox = 150, oy = 270;
  const isoX = (x, y, z) => ox + x * 44 - y * 28;
  const isoY = (x, y, z) => oy - z * 40 + y * 18;
  const saddle = (x, y) => 1.5 + 0.4 * x * x - 0.4 * y * y;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Critical Points — Maxima, Minima, and Saddle Points</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Saddle surface */}
        <rect x={8} y={8} width={440} height={H - 16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={228} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Saddle Surface z = x² − y²</text>

        {[-2, -1.5, -1, -0.5, 0, 0.5, 1, 1.5, 2].map(xi => {
          const pts = [];
          for (let yi = -2; yi <= 2; yi += 0.25) {
            const z = saddle(xi, yi);
            if (Math.abs(z - 1.5) < 1.8) pts.push([isoX(xi, yi, z), isoY(xi, yi, z)]);
          }
          return <path key={'sx' + xi} d={pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')} fill="none" stroke="#b8d4f0" strokeWidth="1" />;
        })}
        {[-2, -1.5, -1, -0.5, 0, 0.5, 1, 1.5, 2].map(yi => {
          const pts = [];
          for (let xi = -2; xi <= 2; xi += 0.25) {
            const z = saddle(xi, yi);
            if (Math.abs(z - 1.5) < 1.8) pts.push([isoX(xi, yi, z), isoY(xi, yi, z)]);
          }
          return <path key={'sy' + yi} d={pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')} fill="none" stroke="#cce0f5" strokeWidth="1.2" />;
        })}

        {/* Saddle point at origin */}
        <circle cx={isoX(0, 0, 1.5)} cy={isoY(0, 0, 1.5)} r={7} fill="#c0392b" stroke="#fff" strokeWidth="2" />
        <text x={isoX(0, 0, 1.5) + 10} y={isoY(0, 0, 1.5) - 8} fontSize="13" fill="#c0392b" fontWeight="700">Saddle Point</text>

        {/* Axes */}
        <line x1={ox} y1={oy} x2={isoX(3, 0, 0)} y2={isoY(3, 0, 0)} stroke="#1F3864" strokeWidth="1.5" />
        <line x1={ox} y1={oy} x2={isoX(0, 3, 0)} y2={isoY(0, 3, 0)} stroke="#1F3864" strokeWidth="1.5" />
        <line x1={ox} y1={oy} x2={isoX(0, 0, 3.5)} y2={isoY(0, 0, 3.5)} stroke="#1F3864" strokeWidth="1.5" />
        <text x={isoX(3.2, 0, 0)} y={isoY(3.2, 0, 0) + 4} fontSize="14" fill="#1F3864" fontWeight="700">x</text>
        <text x={isoX(0, 3.2, 0) - 14} y={isoY(0, 3.2, 0) + 4} fontSize="14" fill="#1F3864" fontWeight="700">y</text>
        <text x={isoX(0, 0, 3.6) - 8} y={isoY(0, 0, 3.6) - 4} fontSize="14" fill="#1F3864" fontWeight="700">z</text>

        {/* Second derivative test note */}
        <rect x={16} y={H - 90} width={420} height={76} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1" />
        <text x={24} y={H - 74} fontSize="13" fill="#1F3864" fontWeight="700">Second Derivative Test:  D = f_xx·f_yy − (f_xy)²</text>
        <text x={24} y={H - 57} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">D &gt; 0, f_xx &gt; 0 → Local minimum</text>
        <text x={24} y={H - 41} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">D &gt; 0, f_xx &lt; 0 → Local maximum</text>
        <text x={24} y={H - 25} fontSize="11" fill="#c0392b" fontFamily="'Courier New',monospace">D &lt; 0           → Saddle point</text>

        {/* Right panel: Lagrange multipliers */}
        <rect x={456} y={8} width={436} height={H - 16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={674} y={30} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Constrained Optimization (Lagrange Multipliers)</text>

        {/* Sketch: ellipse constraint with contour circle */}
        {[1.0, 1.8, 2.5].map((r, i) => (
          <circle key={i} cx={674} cy={185} r={r * 28} fill="none" stroke={['#2E75B6', '#27ae60', '#e07b00'][i]} strokeWidth={i === 1 ? 2.5 : 1.5} strokeDasharray={i === 1 ? '' : '5,3'} />
        ))}
        {/* Constraint ellipse */}
        <ellipse cx={674} cy={185} rx={90} ry={55} fill="none" stroke="#c0392b" strokeWidth="2.5" />
        <text x={674 + 92} y={185 + 4} fontSize="12" fill="#c0392b" fontWeight="700">g(x,y)=0</text>
        {/* Tangency point */}
        <circle cx={674 + 50} cy={185 - 43} r={6} fill="#9b59b6" stroke="#fff" strokeWidth="2" />
        <text x={674 + 56} y={185 - 50} fontSize="12" fill="#9b59b6" fontWeight="700">optimum</text>

        {/* Formula box */}
        <rect x={464} y={260} width={420} height={88} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1" />
        <text x={472} y={276} fontSize="13" fill="#1F3864" fontWeight="700">Lagrange Condition</text>
        <text x={472} y={294} fontSize="12" fill="#2E75B6" fontFamily="'Courier New',monospace">∇f = λ ∇g</text>
        <text x={472} y={312} fontSize="11" fill="#444">Optimise f(x,y) subject to g(x,y) = 0</text>
        <text x={472} y={329} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">∂f/∂x = λ·∂g/∂x  AND  ∂f/∂y = λ·∂g/∂y</text>
        <text x={472} y={344} fontSize="11" fill="#555">λ is the Lagrange multiplier — sensitivity of optimum to constraint</text>
      </svg>
      <div style={S.dCaption}>
        Critical points occur where ∇f = 0. The second derivative test (discriminant D) classifies them as minima, maxima, or saddle points. For constrained problems, Lagrange multipliers enforce that the gradient of the objective is parallel to the gradient of the constraint at the optimum — the contour and constraint curves are tangent at that point.
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────
export default function MultivariableCalculus() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Multivariable Calculus</h1>
      <p style={S.p}>
        Multivariable calculus extends the tools of single-variable calculus — derivatives, integrals, and optimisation — to functions of two or more variables. Where single-variable calculus describes curves, multivariable calculus describes surfaces, volumes, and fields. It is the language of heat transfer, fluid mechanics, electromagnetism, structural analysis, and any engineering problem where more than one independent variable is in play simultaneously.
      </p>

      <InfoBox title="Core Idea: Everything generalises from one variable to many" items={[
        "f(x,y) defines a surface in 3D — every point (x,y) maps to a height z",
        "Partial derivative ∂f/∂x: slope in x-direction with y held fixed",
        "Gradient ∇f = (∂f/∂x, ∂f/∂y): vector pointing in direction of steepest ascent",
        "Double integral ∬f dA: volume under a surface over a 2D region",
        "Line integral ∫_C F·dr: total work done by a vector field along a path",
        "Divergence theorem & Stokes' theorem: connect volume integrals to surface/line integrals",
      ]} />

      <h2 style={S.h2}>Partial Derivatives & the Gradient</h2>
      <DiagramPartialDerivatives />
      <Table2 rows={[
        ["Partial derivative ∂f/∂x", "Differentiate f(x,y) with respect to x, treating y as a constant\nNotation: f_x or ∂f/∂x"],
        ["Partial derivative ∂f/∂y", "Differentiate f(x,y) with respect to y, treating x as a constant\nNotation: f_y or ∂f/∂y"],
        ["Gradient ∇f (2D)", "∇f = (∂f/∂x, ∂f/∂y)\nPoints in the direction of steepest increase; magnitude = rate of steepest ascent"],
        ["Gradient ∇f (3D)", "∇f = (∂f/∂x, ∂f/∂y, ∂f/∂z)\nAlways perpendicular to level surfaces of f"],
        ["Directional derivative", "D_u f = ∇f · û  (û = unit vector in direction u)\nRate of change of f in direction u"],
        ["Chain rule (multivariable)", "dz/dt = (∂z/∂x)(dx/dt) + (∂z/∂y)(dy/dt)\nUsed when x and y are themselves functions of t"],
        ["Total differential", "df = (∂f/∂x)dx + (∂f/∂y)dy\nApproximate change in f for small changes in x and y"],
        ["Higher-order partials", "f_xx = ∂²f/∂x²,  f_xy = ∂²f/∂x∂y\nClairaut's Theorem: f_xy = f_yx when both are continuous"],
      ]} />

      <h2 style={S.h2}>Level Curves & Contour Maps</h2>
      <DiagramLevelCurves />
      <Table2 rows={[
        ["Level curve", "Set of all (x,y) where f(x,y) = c for a constant c\nIn 3D: a horizontal slice of the surface at height c"],
        ["Contour map", "Collection of level curves at evenly spaced values of c\nStandard in topography, heat maps, FEA stress plots"],
        ["Gradient ⊥ level curves", "∇f is always perpendicular to level curves\nSteepest descent follows the negative gradient"],
        ["Level surface (3D)", "f(x,y,z) = c defines a surface in 3D space\nExample: pressure = constant in a fluid"],
        ["Spacing interpretation", "Tightly spaced contours → steep gradient\nWidely spaced contours → gentle slope"],
      ]} />

      <h2 style={S.h2}>Double & Triple Integrals</h2>
      <DiagramDoubleIntegral />
      <Table2 rows={[
        ["Double integral", "∬_R f(x,y) dA = volume under surface z=f(x,y) over region R\nFor f=1: result is the area of R"],
        ["Iterated (rectangular)", "∫_a^b ∫_c^d f(x,y) dy dx\nEvaluate inner integral first (treat x as constant), then outer"],
        ["Fubini's Theorem", "∫_a^b ∫_c^d f dy dx = ∫_c^d ∫_a^b f dx dy\nOrder of integration can be swapped for continuous f"],
        ["Polar coordinates", "∬f(x,y) dA = ∫∫ f(r cosθ, r sinθ) r dr dθ\nUse when region R is circular or has angular symmetry"],
        ["Triple integral", "∭_V f(x,y,z) dV = volume/mass of 3D region V\nUsed in centre of mass, moment of inertia, fluid volume calculations"],
        ["Jacobian (coord. change)", "∬f dA → ∬f(u,v) |J| du dv\n|J| = |∂(x,y)/∂(u,v)| accounts for stretching of coordinates"],
        ["Average value", "f_avg = (1/A) ∬_R f(x,y) dA\nA = area of region R"],
      ]} />

      <h2 style={S.h2}>Vector Fields & Line Integrals</h2>
      <DiagramVectorField />
      <Table2 rows={[
        ["Vector field", "F(x,y) = (P(x,y), Q(x,y)) — a vector assigned to every point\nExamples: gravitational field, electric field, fluid velocity"],
        ["Line integral (scalar)", "∫_C f ds — integrates scalar f along path C with arc-length weighting\nResult: area of curtain hanging from C up to surface f"],
        ["Line integral (vector)", "∫_C F · dr = ∫_C (P dx + Q dy) — work done by F along C\nSign depends on direction of travel"],
        ["Conservative field", "F = ∇φ for some scalar potential φ\nLine integral is path-independent; ∮_C F·dr = 0 for any closed loop"],
        ["Curl (2D test)", "∂Q/∂x − ∂P/∂y = 0 ↔ F is conservative (in simply connected domain)\nCurl measures local rotation of the field"],
        ["Green's Theorem", "∮_C (P dx + Q dy) = ∬_R (∂Q/∂x − ∂P/∂y) dA\nConverts closed line integral to double integral over enclosed region R"],
        ["Divergence theorem (3D)", "∯_S F · dS = ∭_V ∇·F dV\nFlux through closed surface = volume integral of divergence"],
        ["Stokes' Theorem (3D)", "∮_C F · dr = ∬_S (∇×F) · dS\nGeneralises Green's Theorem to 3D surfaces"],
      ]} />

      <h2 style={S.h2}>Optimisation of Multivariable Functions</h2>
      <DiagramOptimization />
      <Table2 rows={[
        ["Critical point condition", "∂f/∂x = 0  AND  ∂f/∂y = 0 simultaneously\nSolve the system to find candidate points"],
        ["Second derivative test", "D = f_xx·f_yy − (f_xy)²\nD>0, f_xx>0 → min;  D>0, f_xx<0 → max;  D<0 → saddle"],
        ["Saddle point", "Neither local max nor min — surface rises in one direction, falls in another\nCommon example: f(x,y) = x² − y²  at the origin"],
        ["Lagrange multipliers", "To optimise f(x,y) subject to g(x,y)=0:\n∇f = λ∇g  →  solve system including constraint equation"],
        ["Lagrange (3D)", "Optimise f(x,y,z) subject to g(x,y,z)=0\n∇f = λ∇g  (three equations + constraint = 4 equations, 4 unknowns)"],
        ["Taylor expansion (2D)", "f ≈ f₀ + f_x·Δx + f_y·Δy + ½(f_xx Δx² + 2f_xy ΔxΔy + f_yy Δy²)\nUsed in linearisation and FEA error analysis"],
      ]} />

      <InfoBox title="Key Takeaway — Which Tool to Use and When" items={[
        "Rate of change in one direction? → Partial derivative ∂f/∂x or ∂f/∂y",
        "Direction of steepest change? → Gradient ∇f",
        "Rate of change in arbitrary direction? → Directional derivative D_u f = ∇f · û",
        "Visualise a 3D surface in 2D? → Level curves / contour map",
        "Volume under a surface over a 2D region? → Double integral ∬f dA",
        "Work done by a force field along a path? → Line integral ∫_C F · dr",
        "Flux of a field through a closed surface? → Divergence theorem",
        "Find max/min of f(x,y) freely? → Set ∇f = 0, apply second derivative test",
        "Optimise with a constraint? → Lagrange multipliers: ∇f = λ∇g",
      ]} />
    </div>
  );
}